import type { ReactNode } from "react"

import { Link } from "react-router-dom"

import {
  describePricingError,
  type RefreshPricingMutation,
} from "./refresh-pricing-action"

const SAVE_MARGIN_ERROR = `Unable to save margin`

/**
 * The System Pricing footer for any proposal kind: Close, the out-of-date
 * notice, and one primary button that does both jobs. An unsaved margin edit
 * makes it Save — the caller's save mutation persists the margin and then
 * refreshes pricing inside its own `mutationFn` — otherwise it is Refresh
 * Pricing. Each failure is explained above the footer.
 */
export function SaveOrRefreshPricingFooter<TSave, TRefresh>({
  save,
  saveVariables,
  refresh,
  refreshVariables,
  dirty,
  canEdit,
  blocked = false,
  outOfDateNotice,
  onClose,
  describeSaveError = () => SAVE_MARGIN_ERROR,
  describeRefreshError = describePricingError,
}: {
  save: RefreshPricingMutation<TSave>
  /** Null while the draft is invalid, which keeps Save disabled. */
  saveVariables: TSave | null
  refresh: RefreshPricingMutation<TRefresh>
  refreshVariables: TRefresh
  dirty: boolean
  /** Whether the primary button shows at all. */
  canEdit: boolean
  /** Setup is missing an input pricing needs: Save and Refresh would fail or store nothing useful. */
  blocked?: boolean
  /** The out-of-date notice, or null while pricing is current. */
  outOfDateNotice: ReactNode
  onClose: () => void
  describeSaveError?: (error: unknown) => string
  /** A kind whose refresh can be refused for a reason the seller fixes says so. */
  describeRefreshError?: (error: unknown) => string
}): JSX.Element {
  const busy = save.isLoading || refresh.isLoading
  const primaryDisabled = busy || blocked || (dirty && saveVariables == null)

  return (
    <>
      {save.isError ? (
        <p
          role="alert"
          className="mt-3 text-[length:var(--step--2)] text-danger"
        >
          {describeSaveError(save.error)}
        </p>
      ) : null}
      {refresh.isError ? (
        <p
          role="alert"
          className="mt-3 text-[length:var(--step--2)] text-danger"
        >
          {describeRefreshError(refresh.error)}
        </p>
      ) : null}
      <div className="mt-6 flex shrink-0 items-center justify-between border-t border-border pt-5">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="rounded-md border border-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          Close
        </button>
        <div className="flex items-center gap-3">
          {outOfDateNotice}
          {canEdit ? (
            <button
              type="button"
              disabled={primaryDisabled}
              onClick={() => {
                if (!dirty) {
                  refresh.mutate(refreshVariables)
                  return
                }
                if (saveVariables == null) {
                  return
                }
                save.mutate(saveVariables)
              }}
              className={
                // A plain refresh is secondary; an unsaved margin edit or
                // stale pricing earns the filled accent treatment.
                dirty || outOfDateNotice != null
                  ? `rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50`
                  : `rounded-md border border-bp-border-strong px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text transition-colors hover:border-bp-accent disabled:cursor-not-allowed disabled:opacity-50`
              }
            >
              {save.isLoading
                ? `Saving`
                : refresh.isLoading
                  ? `Refreshing`
                  : dirty
                    ? `Save`
                    : `Refresh Pricing`}
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

/** A step that blocks pricing, linked to where it is finished when it can be. */
export type PricingSetupGapLink = {
  key: string
  label: string
  href: string | null
  /** The link's text, e.g. "Open Equipment"; needed when `href` is set. */
  action: string | null
}

const GAP_LINK_CLASS = `font-medium text-foreground underline underline-offset-2 hover:text-foreground-soft`

/**
 * What still blocks Calculate Pricing. `lead` is a kind's own sentence for a
 * gap it explains apart (Battery's Utility); the list then reads "Also
 * finish:".
 */
export const PricingSetupGaps = ({
  gaps,
  lead = null,
}: {
  gaps: readonly PricingSetupGapLink[]
  lead?: ReactNode
}): JSX.Element => (
  <div
    role="status"
    className="flex flex-col gap-2 text-[length:var(--step--2)] text-muted"
  >
    {lead}
    {gaps.length > 0 ? (
      <div className="flex flex-col gap-1.5">
        <p>
          {lead != null
            ? `Also finish:`
            : `Finish setup before calculating pricing:`}
        </p>
        <ul className="flex list-disc flex-col gap-1 pl-5">
          {gaps.map((gap) => (
            <li key={gap.key}>
              <span className="text-foreground-soft">{gap.label}</span>
              {gap.href != null && gap.action != null ? (
                <>
                  {` — `}
                  <Link to={gap.href} className={GAP_LINK_CLASS}>
                    {gap.action}
                  </Link>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    ) : null}
  </div>
)

/** Battery's Utility lead uses the same link treatment as the list. */
export const PricingSetupGapLeadLink = ({
  href,
  children,
}: {
  href: string
  children: ReactNode
}): JSX.Element => (
  <Link to={href} className={GAP_LINK_CLASS}>
    {children}
  </Link>
)
